import { useCallback, useMemo, useState, useEffect } from 'react'
import { parseDateEvent } from './Utils.js'

export const useUpcomingEventStatus = (eventDateStart, eventDateEnd) => {
    const [currentTime, setCurrentTime] = useState(new Date())
    
    const calc = useCallback(() => {
        if (!eventDateStart || !eventDateStart[3]) {
            return { isLive: false, isUpcoming: false, upcomingText: null, isCountdown: false }
        }

        const { eventParsed: eventParsedStartDay, isAllDayEvent } = parseDateEvent(eventDateStart[3])
        
        let isLive = false
        let isUpcoming = false
        let upcomingText = null
        let isCountdown = false

        if (!isAllDayEvent) {
            // eventDateEnd should always exist as it defaults to eventDateStart in EventComposer
            const { eventParsed: eventParsedEndDay } = parseDateEvent(eventDateEnd[3])
            // Check if event is currently live
            isLive = currentTime >= eventParsedStartDay && currentTime <= eventParsedEndDay
        } else {
            // For all-day events, consider live if same day
            isLive = currentTime.toDateString() === eventParsedStartDay.toDateString()
        }

        // If not live, check if upcoming
        if (!isLive && eventParsedStartDay > currentTime) {
            isUpcoming = true
            
            // Calculate time difference
            const timeDiff = eventParsedStartDay.getTime() - currentTime.getTime()
            const totalSeconds = Math.floor(timeDiff / 1000)
            const minutes = Math.floor(timeDiff / (1000 * 60))
            const hours = Math.floor(timeDiff / (1000 * 60 * 60))
            const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24))

            // Format upcoming text based on time remaining
            if (days >= 1 && days < 7) {
                upcomingText = `${days} ${days === 1 ? 'day' : 'days'} out`
            } else if (hours >= 2 && hours < 24) {
                // If more than 2 hours, show hours
                upcomingText = `${hours} hours out`
            } else if ((hours >= 1 || minutes >= 1) && hours < 2) {
                // For events 2 hours or less, show live countdown
                isCountdown = true
                const remainingHours = Math.floor(totalSeconds / 3600)
                const remainingMinutes = Math.floor((totalSeconds % 3600) / 60)
                const remainingSeconds = totalSeconds % 60
                
                if (remainingHours > 0) {
                    upcomingText = `${remainingHours}:${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
                } else if (remainingMinutes > 0) {
                    upcomingText = `${remainingMinutes}:${remainingSeconds.toString().padStart(2, '0')}`
                } else {
                    upcomingText = `0:${remainingSeconds.toString().padStart(2, '0')}`
                }
            } else {
                // Don't show upcoming badge for events more than a week away or less than a minute away
                isUpcoming = false
            }
        }

        return { isLive, isUpcoming, upcomingText, isCountdown }
    }, [eventDateStart, eventDateEnd, currentTime])

    const eventStatus = useMemo(calc, [calc])
    
    // Set up interval for countdown updates when event is within 1 hour
    useEffect(() => {
        if (eventStatus.isCountdown) {
            const interval = setInterval(() => {
                setCurrentTime(new Date())
            }, 1000)
            
            return () => clearInterval(interval)
        }
    }, [eventStatus.isCountdown])
    
    return eventStatus
}